import { inc } from 'semver';
import lt from 'semver/functions/lt';
import maxSatisfying from 'semver/ranges/max-satisfying';
import prerelease from 'semver/functions/prerelease';
import valid from 'semver/functions/valid';
import type { ReadonlyDeep } from 'type-fest';

import type { IconName } from '@viamrobotics/prime-core';
import {
  type Module as APIModule,
  ModuleSourceType,
  ModuleVersion,
  type VersionHistory,
  Visibility,
} from '@viamrobotics/web-client/app/v1/app';

import { timestampToDate } from './api/timestamp';
import type { NOT_YET_PUBLISHED } from './registry-item';
import type {
  ReadonlyLocalModule,
  ReadonlyRegistryModule,
} from './robot-config';

export interface LocalModuleConfig {
  type: 'local';
  name: string;
  executable_path: string;
}

export interface DeployedModuleConfig {
  type: 'registry';
  name: string;
  module_id: string;
  version: string;
}

export type ModuleConfig = LocalModuleConfig | DeployedModuleConfig;

export interface RegistryModuleSummary extends APIModule {
  author: string;
  registryURL: string;
  resourceTypes: string[];
  lastUpdated: Date | typeof NOT_YET_PUBLISHED;
  supportedPlatforms: PlatformBreadcrumbs[];
  /** Link to a previous module ID if the module has been transferred */
  previousID: string | undefined;
  /** The source type of the module (external, viam_hosted, etc.) */
  sourceType?: ModuleSourceType;
}

export interface ModuleDeprecation {
  /** The module itself is deprecated. */
  isModuleDeprecated: boolean;
  /** The specific version the module resolves to is deprecated. */
  isVersionDeprecated: boolean;
  /** Either the module or its resolved version is deprecated. */
  isDeprecated: boolean;
}

/**
 * Determines whether a configured module — or the specific version it resolves to —
 * is deprecated. Used to surface deprecation on the machine config page.
 */
export const getModuleDeprecation = (
  summary:
    | Pick<RegistryModuleSummary, 'deprecatedStatus' | 'versions'>
    | undefined,
  resolvedVersion: string | undefined
): ModuleDeprecation => {
  const isModuleDeprecated = Boolean(summary?.deprecatedStatus);
  const isVersionDeprecated = Boolean(
    summary?.versions.find((version) => version.version === resolvedVersion)
      ?.deprecatedStatus
  );
  return {
    isModuleDeprecated,
    isVersionDeprecated,
    isDeprecated: isModuleDeprecated || isVersionDeprecated,
  };
};

/**
 * The version a configured module actually runs. Used to resolve a bug where on a subpart,
 * when a newer (deprecated) version overrides, the resources are labelled based on the
 * surpressed version's deprecated status rather than the effective version.
 */
export const getEffectiveModuleVersion = (
  deployed: { resolvedVersion?: string; overridingVersion?: string } | undefined
): string | undefined =>
  deployed?.overridingVersion ?? deployed?.resolvedVersion ?? undefined;

export type PlatformBreadcrumbs =
  | [os: string, arch: string, ...additional: string[]]
  | [os: string];

export const isDeployedModule = (
  mod: ModuleConfig
): mod is DeployedModuleConfig => {
  return mod.type === 'registry';
};

export const isLocalModuleConfig = (
  mod: ModuleConfig
): mod is LocalModuleConfig => {
  return mod.type === 'local';
};

export const UploadedAtFormatter = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});

/**
 * Given an module platform, returns the breadcrumbs that should be displayed to the user.
 *
 * ```ts
 * 'linux/arm64' => ['linux', 'arm64]
 * 'darwin/amd64'        => ['darwin', 'amd64]
 * ```
 * @param platform A string from a module version file
 * @returns A list of two strings
 */
export const getPlatformBreadcrumbs = (
  platform: string
): PlatformBreadcrumbs => {
  const split = platform.split('/');

  if (split.length >= 2) {
    return split as PlatformBreadcrumbs;
  }

  return [platform];
};

/**
 * Given a list of versions, check all of the files to get the most recent
 * `uploadedAt` time as a `Date`. If there is no `uploadedAt` present, returns
 * undefined.
 *
 * @param versions A list of module versions
 * @returns The most recent `uploadedAt` as a `Date` or undefined
 */
export const getMostRecentUploadDate = (
  versions: ReadonlyDeep<VersionHistory[] | ModuleVersion[]>
) =>
  filterPrerelease(filterReloadVersion(versions))
    .flatMap(({ files }) => files)
    .map(({ uploadedAt }) =>
      uploadedAt ? timestampToDate(uploadedAt) : undefined
    )
    .filter((maybeDate): maybeDate is Date => maybeDate !== undefined)
    .sort((a, b) => b.getTime() - a.getTime())[0];

/**
 * Given a list of versions, get the most recent version
 *
 * @param versions A list of module versions
 * @returns The most recent version
 */
export const getMostRecentVersion = (
  versions: ReadonlyDeep<VersionHistory[] | ModuleVersion[]>
) => filterPrerelease(filterReloadVersion(versions)).at(-1);

/**
 * Given a list of versions, get the most recent version including prereleases
 *
 * @param versions A list of module versions
 * @returns The most recent version
 */
export const getMostRecentVersionWithPrerelease = (
  versions: ReadonlyDeep<VersionHistory[] | ModuleVersion[]>
) => filterReloadVersion(versions).at(-1);

/**
 * Client-side approximation of the version a configured module resolves to. The machine
 * config gets this from the backend best-version API; fragments have no such API, so the
 * fragment page resolves "latest"/semver-range strings against published versions itself.
 */
export const resolveConfiguredModuleVersion = (
  configuredVersion: string | undefined,
  versions: ReadonlyDeep<VersionHistory[] | ModuleVersion[]>
): string | undefined => {
  if (configuredVersion === 'latest-with-prerelease') {
    return getMostRecentVersionWithPrerelease(versions)?.version;
  }
  if (configuredVersion === undefined || configuredVersion === 'latest') {
    return getMostRecentVersion(versions)?.version;
  }
  const candidates = filterReloadVersion(versions).map(
    ({ version }) => version
  );
  return maxSatisfying(candidates, configuredVersion) ?? undefined;
};

/**
 * Given a list of versions, filter out versions that are not in the format X.Y.Z
 * so that release candidate versions are excluded.
 *
 * Except if there is only prerelease versions, in this case the first version is returned
 *
 * @param versions A list of module versions
 * @returns The most recent version
 */
const filterPrerelease = (
  versions: ReadonlyDeep<VersionHistory[] | ModuleVersion[]>
) => {
  const filtered = versions.filter(({ version }) => !prerelease(version));
  if (filtered.length === 0 && versions.length > 0) {
    return versions;
  }
  return filtered;
};

export const filterReloadVersion = (
  versions: ReadonlyDeep<VersionHistory[] | ModuleVersion[]>
) => {
  return versions.filter(({ version }) => !version.startsWith('reload'));
};

/**
 * Given a list of versions, return the select options for each version
 *
 * @param versions A list of module versions
 * @returns A list of version detailed options
 */
export const getVersionOptions = (
  versions: ReadonlyDeep<VersionHistory[] | ModuleVersion[]>
) => {
  const mostRecentVersion = getMostRecentVersion(versions);

  const options = versions.map((version) => {
    const isLatest = version === mostRecentVersion;
    const uploadDate = getMostRecentUploadDate([version]);
    const formattedDate = UploadedAtFormatter.format(uploadDate);
    return {
      label: `${isLatest ? 'Latest - ' : ''}${version.version} (${formattedDate})`,
      value: version.version,
      uploadDate,
      isLatest,
    };
  });
  // sort in descending order, showing latest first and then previous versions
  return options
    .sort((a, b) => {
      if (a.isLatest && !b.isLatest) return -1;
      if (!a.isLatest && b.isLatest) return 1;
      return (b.uploadDate?.getTime() ?? 0) - (a.uploadDate?.getTime() ?? 0);
    })
    .map(({ label, value }) => ({ label, value }));
};

/**
 * Given a list of platforms for a specific version, return the select options for each platform
 *
 * @param versions A list of module versions
 * @returns A list of platform detailed options
 */
export const getPlatformOptionsForVersion = (
  version: ReadonlyDeep<VersionHistory | ModuleVersion> | undefined
) => {
  if (version === undefined) {
    return [];
  }

  return version.files.map(({ platform }) => {
    // split into os and arch strings
    const breadcrums = getPlatformBreadcrumbs(platform);

    // show icons only for darwin and linux, other OS like wildcard 'any' gets 'application-outline' icon.
    let icon: IconName;
    switch (breadcrums[0]) {
      case 'darwin': {
        icon = 'apple';
        break;
      }
      case 'linux': {
        icon = 'linux';
        break;
      }
      default: {
        icon = 'application-outline';
      }
    }

    return {
      icon,
      // only show one breakcrumb for 'any' wildcard
      label:
        breadcrums[0] + (breadcrums.length === 1 ? '' : ` > ${breadcrums[1]}`),
      value: platform,
    };
  });
};

export const TEST_ONLY = {
  EMPTY_MODULE_SUMMARY: {
    author: '',
    name: 'empty-module',
    totalRobotUsage: '0',
    totalOrganizationUsage: '0',
    url: 'http://foo.bar/baz',
    moduleId: '',
    lastUpdated: new Date(),
    resourceTypes: [],
    registryURL: '',
    description: '',
    entrypoint: '',
    models: [],
    apps: [],
    organizationId: '',
    publicNamespace: '',
    supportedPlatforms: [],
    versions: [],
    visibility: Visibility.PRIVATE,
    previousID: undefined,
  } satisfies RegistryModuleSummary,
};

export const DefaultVersion = '0.0.1';
export const DefaultPlatform = 'linux/arm64';

export const getAssumedNextVersionAndPlatform = (
  versions: ReadonlyDeep<VersionHistory[]>
): { version: string; platform: string } => {
  const [latestVersion, ...previousVersions] = versions;

  // If there are no versions then use default version and platform
  if (!latestVersion) {
    return { version: DefaultVersion, platform: DefaultPlatform };
  }

  // Don't use prerelease versions to suggest the next version/platform
  const previousVersionsNonRC = previousVersions.filter(
    (version) => !prerelease(version.version)
  );

  // If there are previous versions then compare latest to the version before it
  // If there have been less platforms in the latest version than previous,
  // then suggest continuing to upload to previous version with the first missing platform
  if (previousVersionsNonRC.length > 0 && previousVersionsNonRC[0]?.files) {
    const mostRecentPreviousFiles = previousVersionsNonRC[0].files;
    for (const file of mostRecentPreviousFiles) {
      if (
        !latestVersion.files.some(
          (latestFile) => latestFile.platform === file.platform
        )
      ) {
        return { version: latestVersion.version, platform: file.platform };
      }
    }
  }

  // Otherwise suggest the next patch version and the first platform of the latest version
  return {
    version: inc(latestVersion.version, 'patch') ?? DefaultVersion,
    platform: latestVersion.files[0]?.platform ?? DefaultPlatform,
  };
};

/**
 * Parses a module ID into its prefix and name.
 * @param moduleID - The module ID to parse.
 * @param fallbackName - Optional name to use when the parsed name is missing
 *   or empty (e.g. when the module ID has no `:` or the portion after it is
 *   empty). When omitted, the parsed name is returned as-is, preserving
 *   `undefined`/`""` semantics for callers that don't want a fallback.
 * @returns The prefix and name. If the prefix is a UUID,
 * it returns the organizationId and name.
 * Otherwise, it returns the namespace and name.
 */
export const parseModuleID = (moduleID: string, fallbackName?: string) => {
  const parts = moduleID.split(':');
  const prefix = parts[0];
  const rawName = parts.length > 1 ? parts[1] : undefined;
  const name = fallbackName !== undefined && !rawName ? fallbackName : rawName;

  // UUID regex pattern (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
  const uuidPattern =
    /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/iu;

  if (uuidPattern.test(prefix ?? '')) {
    return { organizationId: prefix, name };
  }

  return { namespace: prefix, name };
};

/** sortModulesByDisplayName returns the modules sorted A->Z by the name shown
 * in the sidebar: the module name for registry modules (parsed from the module
 * ID) and the resource name for local modules. Sorting by the display name
 * rather than the underlying resource name keeps the list ordered by the
 * visible label.
 */
export const sortModulesByDisplayName = <
  T extends ReadonlyRegistryModule | ReadonlyLocalModule,
>(
  modules: readonly T[]
): T[] => {
  const displayName = (mod: T): string =>
    mod.type === 'registry-module'
      ? (parseModuleID(mod.moduleID).name ?? mod.name)
      : mod.name;

  return [...modules].sort((a, b) =>
    displayName(a).localeCompare(displayName(b))
  );
};

/**
 * Whether a machine's running viam-server is older than a module's minimum
 * required version. False when either version is missing or unparseable.
 */
export const isViamServerBelowMinimum = (
  minimumVersion: string | undefined,
  runningVersion: string | undefined
): boolean => {
  const minimum = valid(minimumVersion ?? null);
  const running = valid(runningVersion ?? null);
  if (!minimum || !running) {
    return false;
  }
  return lt(running, minimum);
};

export { ModuleSourceType } from '@viamrobotics/web-client/app/v1/app';
